<!-- src/apps/secret/conceal/HomepageContent.vue -->

<script setup lang="ts">
  import HomepageTaglines from '@/apps/secret/components/conceal/HomepageTaglines.vue';
  import SecretForm from '@/apps/secret/components/form/SecretForm.vue';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { useLocalReceiptStore } from '@/shared/stores/localReceiptStore';
  import { storeToRefs } from 'pinia';

  const bootstrapStore = useBootstrapStore();
  const { authenticated, ui } = storeToRefs(bootstrapStore);

  const localReceiptStore = useLocalReceiptStore();
</script>

<template>
  <div class="relative min-w-[320px]">
    <!-- Hero Section -->
    <div class="mx-auto max-w-2xl px-4 py-8 sm:py-12 md:py-16">
      <!-- Taglines for non-authenticated users -->
      <HomepageTaglines
        v-if="!authenticated"
        class="mb-8 md:mb-12" />

      <!-- Secret Form -->
      <div v-if="ui?.enabled !== false" class="w-full">
        <SecretForm
          :with-recipient="false"
          :with-asterisk="true"
          :with-generate="true"
          :workspace-mode="localReceiptStore.workspaceMode" />
      </div>

      <!-- Features Section -->
      <div v-if="!authenticated" class="mt-12 md:mt-16">
        <div class="grid gap-6 sm:grid-cols-2">
          <!-- Feature 1 -->
          <div class="transition-all duration-200 rounded-lg border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md dark:border-gray-700 dark:bg-gray-800/50 dark:shadow-none dark:hover:shadow-lg">
            <div class="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100 dark:bg-brand-900/30">
              <svg
                class="h-6 w-6 text-brand-600 dark:text-brand-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 class="mb-2 font-semibold text-gray-900 dark:text-white">
              {{ $t('web.homepage.self_destruct') }}
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ $t('web.homepage.messages_automatically_expire') }}
            </p>
          </div>

          <!-- Feature 2 -->
          <div class="transition-all duration-200 rounded-lg border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md dark:border-gray-700 dark:bg-gray-800/50 dark:shadow-none dark:hover:shadow-lg">
            <div class="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100 dark:bg-brand-900/30">
              <svg
                class="h-6 w-6 text-brand-600 dark:text-brand-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.658 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <h3 class="mb-2 font-semibold text-gray-900 dark:text-white">
              {{ $t('web.homepage.one_time_access') }}
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ $t('web.homepage.accessible_once') }}
            </p>
          </div>

          <!-- Feature 3 -->
          <div class="transition-all duration-200 rounded-lg border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md dark:border-gray-700 dark:bg-gray-800/50 dark:shadow-none dark:hover:shadow-lg">
            <div class="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100 dark:bg-brand-900/30">
              <svg
                class="h-6 w-6 text-brand-600 dark:text-brand-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h3 class="mb-2 font-semibold text-gray-900 dark:text-white">
              {{ $t('web.homepage.protection') }}
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ $t('web.homepage.keep_sensitive_info_out_of_emails') }}
            </p>
          </div>

          <!-- Feature 4 -->
          <div class="transition-all duration-200 rounded-lg border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md dark:border-gray-700 dark:bg-gray-800/50 dark:shadow-none dark:hover:shadow-lg">
            <div class="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100 dark:bg-brand-900/30">
              <svg
                class="h-6 w-6 text-brand-600 dark:text-brand-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 class="mb-2 font-semibold text-gray-900 dark:text-white">
              {{ $t('web.homepage.easy_to_use') }}
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ $t('web.homepage.no_account_necessary') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
